// src/components/dashboard/cases/newCase/case_id/caseTable/components/ScreenshotEditor.js
//
// ✅ NEW: screenshot editor for the STL viewer
// - shows the captured screenshot (nothing is saved to the computer)
// - optional drawing: pen, arrow, box, colours, sizes, undo, clear
// - "Add Note" sends the image to CaseTab, which opens the Add Note modal
//   with the screenshot already attached (like a Ctrl+V paste)
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/* ------------------------------------------------------------------
   Events shared with CaseTab.jsx (keep the names in sync)
   ------------------------------------------------------------------ */
export const STL_SHOT_PROBE_EVENT = "stl-screenshot:can-add-note";
export const STL_SHOT_ADD_NOTE_EVENT = "stl-screenshot:add-note";
// ✅ NEW: a header button (outside the viewer) asks the open viewer to take a screenshot
export const STL_CAPTURE_REQUEST_EVENT = "stl-viewer:capture-request";

export const requestStlScreenshot = () => {
  try {
    const ev = new CustomEvent(STL_CAPTURE_REQUEST_EVENT, { cancelable: true });
    return !window.dispatchEvent(ev); // true when a viewer handled it
  } catch {
    return false;
  }
};

// true when a page (CaseTab) is listening and can open the note modal
export const canAddScreenshotNote = () => {
  try {
    const ev = new CustomEvent(STL_SHOT_PROBE_EVENT, { cancelable: true });
    return !window.dispatchEvent(ev); // a listener calls preventDefault()
  } catch {
    return false;
  }
};

// hand the screenshot file to CaseTab; returns true when it was accepted
export const sendScreenshotToNote = (file) => {
  try {
    const ev = new CustomEvent(STL_SHOT_ADD_NOTE_EVENT, {
      cancelable: true,
      detail: { file },
    });
    return !window.dispatchEvent(ev);
  } catch {
    return false;
  }
};

/* ------------------------------------------------------------------
   Capture helpers used by STLViewer
   ------------------------------------------------------------------ */

// first non-transparent background behind the viewer (the canvas itself is transparent)
export const getViewerBackground = (el) => {
  let n = el;
  while (n && n.nodeType === 1) {
    const bg = window.getComputedStyle(n).backgroundColor;
    if (bg && bg !== "transparent" && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(bg)) {
      return bg;
    }
    n = n.parentElement;
  }
  return "#00a1d6";
};

// the measurement label is an HTML element, so draw it onto the screenshot too
export const drawMeasureLabelOnto = (canvas, labelEl, mountEl) => {
  try {
    if (!canvas || !labelEl || !mountEl) return;
    if (labelEl.style.display === "none" || !labelEl.textContent) return;

    const glCanvas = mountEl.querySelector("canvas");
    if (!glCanvas) return;

    const cr = glCanvas.getBoundingClientRect();
    const lr = labelEl.getBoundingClientRect();
    if (!cr.width || !lr.width) return;

    const s = canvas.width / cr.width;
    const x = (lr.left - cr.left) * s;
    const y = (lr.top - cr.top) * s;
    const w = lr.width * s;
    const h = lr.height * s;
    const r = 8 * s;

    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = "rgba(25, 118, 210, 0.95)";
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "#ffffff";
    ctx.font = `700 ${13 * s}px "SF Mono", Consolas, Monaco, monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(labelEl.textContent, x + w / 2, y + h / 2);
    ctx.restore();
  } catch {
    /* label is optional */
  }
};

/* ------------------------------------------------------------------
   Editor UI
   ------------------------------------------------------------------ */
const COLORS = ["#ff3b30", "#ffcc00", "#34c759", "#007aff", "#ffffff", "#111111"];
const SIZES = [
  { id: "s", px: 3, label: "S" },
  { id: "m", px: 6, label: "M" },
  { id: "l", px: 10, label: "L" },
];
const TOOLS = [
  { id: "pen", label: "Draw", icon: "pen" },
  { id: "arrow", label: "Arrow", icon: "arrow" },
  { id: "rect", label: "Box", icon: "rect" },
];

const Icon = ({ name, size = 16 }) => {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "pen":
      return (
        <svg {...p}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case "arrow":
      return (
        <svg {...p}>
          <line x1="5" y1="19" x2="19" y2="5" />
          <polyline points="9 5 19 5 19 15" />
        </svg>
      );
    case "rect":
      return (
        <svg {...p}>
          <rect x="4" y="5" width="16" height="14" rx="1.5" />
        </svg>
      );
    case "undo":
      return (
        <svg {...p}>
          <polyline points="9 14 4 9 9 4" />
          <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
        </svg>
      );
    case "trash":
      return (
        <svg {...p}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      );
    case "copy":
      return (
        <svg {...p}>
          <rect x="9" y="9" width="12" height="12" rx="2" />
          <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
        </svg>
      );
    case "note":
      return (
        <svg {...p}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <line x1="12" y1="7" x2="12" y2="13" />
          <line x1="9" y1="10" x2="15" y2="10" />
        </svg>
      );
    case "camera":
      return (
        <svg {...p}>
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      );
    case "x":
      return (
        <svg {...p}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    default:
      return null;
  }
};

export const CameraIcon = (props) => <Icon name="camera" {...props} />;

const drawShape = (ctx, s) => {
  ctx.save();
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (s.type === "pen") {
    const pts = s.points;
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, s.width / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
  } else if (s.type === "rect") {
    const x = Math.min(s.a.x, s.b.x);
    const y = Math.min(s.a.y, s.b.y);
    ctx.strokeRect(x, y, Math.abs(s.b.x - s.a.x), Math.abs(s.b.y - s.a.y));
  } else if (s.type === "arrow") {
    const { a, b } = s;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const head = Math.max(12, s.width * 4);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7), b.y - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7), b.y - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
};

const EDITOR_CSS = `
  .sse-overlay {
    position: fixed; inset: 0; z-index: 12000;
    background: rgba(8, 15, 30, 0.78);
    display: flex; align-items: center; justify-content: center;
    padding: 12px; box-sizing: border-box;
    animation: sse-fade .18s ease-out;
  }
  @keyframes sse-fade { from { opacity: 0; } to { opacity: 1; } }
  .sse-panel {
    width: min(1200px, 100%); max-height: 100%;
    display: flex; flex-direction: column;
    background: #fff; border-radius: 12px; overflow: hidden;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
  }
  .sse-head {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px;
    background: linear-gradient(115deg, #004aad 0%, #003b88 100%);
    color: #fff; flex-shrink: 0;
  }
  .sse-head-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.22);
    flex: 0 0 auto;
  }
  .sse-title { font-weight: 700; font-size: 15px; line-height: 1.2; }
  .sse-sub { font-size: 11px; opacity: .78; margin-top: 2px; }
  .sse-close {
    margin-left: auto; width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.22); background: rgba(255,255,255,0.12);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; flex: 0 0 auto;
  }
  .sse-close:hover { background: rgba(255,255,255,0.28); }

  .sse-tools {
    display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px;
    padding: 8px 12px; border-bottom: 1px solid #e3ecfb; background: #f4f8fe;
    flex-shrink: 0;
  }
  .sse-group { display: flex; align-items: center; gap: 4px; }
  .sse-sep { width: 1px; height: 24px; background: #d3e0f5; }
  .sse-tool {
    display: inline-flex; align-items: center; gap: 5px;
    border: 1px solid #c9d7ee; background: #fff; color: #1c2b45;
    border-radius: 7px; padding: 5px 9px; font-size: 12px; font-weight: 600;
    cursor: pointer; white-space: nowrap;
  }
  .sse-tool:hover:not(:disabled) { background: #eef4fb; }
  .sse-tool.is-on { background: #004aad; border-color: #004aad; color: #fff; }
  .sse-tool:disabled { opacity: .45; cursor: not-allowed; }
  .sse-swatch {
    width: 22px; height: 22px; border-radius: 50%;
    border: 2px solid #fff; box-shadow: 0 0 0 1px #b8c4d6;
    cursor: pointer; padding: 0;
  }
  .sse-swatch.is-on { box-shadow: 0 0 0 2px #004aad; transform: scale(1.12); }
  .sse-hint { font-size: 11px; color: #6b7a93; margin-left: auto; }

  .sse-stage {
    flex: 1 1 auto; min-height: 0; overflow: auto;
    display: flex; align-items: center; justify-content: center;
    background: #1d2433; padding: 10px;
  }
  .sse-canvas {
    display: block; max-width: 100%;
    max-height: calc(100vh - 230px);
    border-radius: 6px; box-shadow: 0 4px 18px rgba(0,0,0,0.4);
    touch-action: none; background: #fff;
  }

  .sse-foot {
    display: flex; align-items: center; justify-content: flex-end; gap: 8px;
    padding: 10px 14px; flex-shrink: 0;
    background: linear-gradient(115deg, #004aad 0%, #003b88 100%);
  }
  .sse-msg { color: #fff; font-size: 12px; opacity: .9; margin-right: auto; }
  .sse-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 7px; padding: 8px 16px; font-size: 13px; font-weight: 700;
    cursor: pointer; white-space: nowrap;
  }
  .sse-btn.ghost { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.55); }
  .sse-btn.ghost:hover { background: rgba(255,255,255,0.14); }
  .sse-btn.primary { background: #fff; color: #004aad; border: 1px solid #fff; }
  .sse-btn.primary:hover:not(:disabled) { background: #eef4fb; }
  .sse-btn:disabled { opacity: .5; cursor: not-allowed; }

  @media (max-width: 640px) {
    .sse-overlay { padding: 6px; }
    .sse-head { padding: 10px; }
    .sse-sub, .sse-hint, .sse-tool-label { display: none; }
    .sse-tools { gap: 6px 8px; padding: 6px 8px; }
    .sse-sep { display: none; }
    .sse-tool { padding: 5px 7px; }
    .sse-swatch { width: 20px; height: 20px; }
    .sse-canvas { max-height: calc(100vh - 200px); }
    .sse-foot { flex-wrap: wrap; padding: 8px 10px; }
    .sse-msg { flex-basis: 100%; }
    .sse-btn { flex: 1; justify-content: center; padding: 8px 10px; font-size: 12px; }
  }
`;

const ScreenshotEditor = ({ image, canAddNote = false, onClose, onAddNote }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const shapesRef = useRef([]);
  const currentRef = useRef(null);

  const [tool, setTool] = useState(null); // null = no drawing
  const [color, setColor] = useState(COLORS[0]);
  const [sizeId, setSizeId] = useState("m");
  const [shapeCount, setShapeCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    shapesRef.current.forEach((s) => drawShape(ctx, s));
    if (currentRef.current) drawShape(ctx, currentRef.current);
  }, []);

  // load the screenshot into the canvas
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      redraw();
    };
    img.src = image;
    shapesRef.current = [];
    setShapeCount(0);
  }, [image, redraw]);

  // Esc closes ONLY this editor (not the 3D viewer behind it)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.stopPropagation();
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.stopImmediatePropagation();
        shapesRef.current.pop();
        setShapeCount(shapesRef.current.length);
        redraw();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, redraw]);

  const toCanvasPoint = (ev) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const s = canvas.width / Math.max(r.width, 1);
    return { x: (ev.clientX - r.left) * s, y: (ev.clientY - r.top) * s, s };
  };

  const onPointerDown = (ev) => {
    if (!tool || ev.button !== 0) return;
    ev.preventDefault();
    const p = toCanvasPoint(ev);
    const px = (SIZES.find((x) => x.id === sizeId) || SIZES[1]).px;
    const width = px * p.s; // same thickness on screen, whatever the image size

    currentRef.current =
      tool === "pen"
        ? { type: "pen", color, width, points: [{ x: p.x, y: p.y }] }
        : { type: tool, color, width, a: { x: p.x, y: p.y }, b: { x: p.x, y: p.y } };

    try {
      ev.currentTarget.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    redraw();
  };

  const onPointerMove = (ev) => {
    const cur = currentRef.current;
    if (!cur) return;
    const p = toCanvasPoint(ev);
    if (cur.type === "pen") cur.points.push({ x: p.x, y: p.y });
    else cur.b = { x: p.x, y: p.y };
    redraw();
  };

  const onPointerUp = () => {
    const cur = currentRef.current;
    if (!cur) return;
    currentRef.current = null;

    const tiny =
      cur.type !== "pen" &&
      Math.hypot(cur.b.x - cur.a.x, cur.b.y - cur.a.y) < 3;
    if (!tiny) {
      shapesRef.current.push(cur);
      setShapeCount(shapesRef.current.length);
    }
    redraw();
  };

  const undo = () => {
    shapesRef.current.pop();
    setShapeCount(shapesRef.current.length);
    redraw();
  };

  const clearAll = () => {
    shapesRef.current = [];
    setShapeCount(0);
    redraw();
  };

  const toFile = () =>
    new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        reject(new Error("Screenshot is not ready"));
        return;
      }
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not create the image"));
          return;
        }
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const name =
          `stl-screenshot-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
          `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.png`;
        resolve(new File([blob], name, { type: "image/png", lastModified: Date.now() }));
      }, "image/png");
    });

  const handleAddNote = async () => {
    setBusy(true);
    setMessage("");
    try {
      const file = await toFile();
      const ok = onAddNote ? onAddNote(file) : false;
      if (!ok) setMessage("Add Note is only available on the case page.");
    } catch (e) {
      setMessage(e.message || "Could not create the screenshot");
    } finally {
      setBusy(false);
    }
  };

  // copy to clipboard (nothing is saved on the computer)
  const handleCopy = async () => {
    setMessage("");
    try {
      const file = await toFile();
      if (!navigator.clipboard || typeof window.ClipboardItem === "undefined") {
        throw new Error("Copy is not supported in this browser");
      }
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": file })]);
      setMessage("Copied to clipboard");
    } catch (e) {
      setMessage(e.message || "Copy failed");
    }
  };

  const stop = (e) => e.stopPropagation();

  return ReactDOM.createPortal(
    <div
      className="sse-overlay"
      onClick={stop}
      onPointerDown={stop}
      onContextMenu={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot"
    >
      <style>{EDITOR_CSS}</style>

      <div className="sse-panel">
        <div className="sse-head">
          <span className="sse-head-icon">
            <Icon name="camera" size={17} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="sse-title">Screenshot</div>
            <div className="sse-sub">
              Draw on it if you want, then add it to a note · not saved on your computer
            </div>
          </div>
          <button type="button" className="sse-close" onClick={onClose} title="Close" aria-label="Close">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="sse-tools">
          <div className="sse-group">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`sse-tool${tool === t.id ? " is-on" : ""}`}
                onClick={() => setTool((cur) => (cur === t.id ? null : t.id))}
                title={t.label}
                aria-pressed={tool === t.id}
              >
                <Icon name={t.icon} size={14} />
                <span className="sse-tool-label">{t.label}</span>
              </button>
            ))}
          </div>

          <span className="sse-sep" />

          <div className="sse-group">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`sse-swatch${color === c ? " is-on" : ""}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                title={c}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>

          <span className="sse-sep" />

          <div className="sse-group">
            {SIZES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`sse-tool${sizeId === s.id ? " is-on" : ""}`}
                onClick={() => setSizeId(s.id)}
                title={`Line size ${s.label}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <span className="sse-sep" />

          <div className="sse-group">
            <button type="button" className="sse-tool" onClick={undo} disabled={shapeCount === 0} title="Undo (Ctrl+Z)">
              <Icon name="undo" size={14} />
              <span className="sse-tool-label">Undo</span>
            </button>
            <button type="button" className="sse-tool" onClick={clearAll} disabled={shapeCount === 0} title="Clear drawing">
              <Icon name="trash" size={14} />
              <span className="sse-tool-label">Clear</span>
            </button>
          </div>

          <span className="sse-hint">
            {tool ? "Drag on the image to draw" : "Pick Draw, Arrow or Box to mark the image"}
          </span>
        </div>

        <div className="sse-stage">
          <canvas
            ref={canvasRef}
            className="sse-canvas"
            style={{ cursor: tool ? "crosshair" : "default" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
        </div>

        <div className="sse-foot">
          {message ? (
            <span className="sse-msg">{message}</span>
          ) : !canAddNote ? (
            <span className="sse-msg">Open the case page to add this screenshot to a note.</span>
          ) : null}

          <button type="button" className="sse-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="sse-btn ghost" onClick={handleCopy}>
            <Icon name="copy" size={14} /> Copy
          </button>
          <button
            type="button"
            className="sse-btn primary"
            onClick={handleAddNote}
            disabled={busy || !canAddNote}
          >
            <Icon name="note" size={14} /> {busy ? "Preparing…" : "Add Note"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScreenshotEditor;